import { EnhancedScrollArea } from "@components/enhanced-scroll-area";
import { CoinsVal } from "@components/icons/coins";
import { ResponsiveSelect } from "@components/responsive-select";
import { ImagesType } from "@features/images/type";
import { useImage } from "@features/images/use-images";
import { useSavedState } from "@features/utils/use-saved-state";
import { Button, Heading, Text, TextArea, Tooltip } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface StudioFaceProps {
  image: ImagesType;
  onAction: (action: string) => void;
}

export type FaceEffectType =
  | "smooth_skin"
  | "increase_realism"
  | "add_imperfections"
  | "remove_freckles"
  | "add_freckles"
  | "makeup";

export type MakeupStyle =
  | "natural"
  | "glamorous"
  | "smokey_eye"
  | "cat_eye"
  | "nude"
  | "bold_lips"
  | "bronzed"
  | "dewy"
  | "matte";

const FACE_EFFECTS: Array<{
  id: FaceEffectType;
  nameKey: string;
  descriptionKey: string;
}> = [
  {
    id: "smooth_skin",
    nameKey: "studio.face.effects.smooth_skin.name",
    descriptionKey: "studio.face.effects.smooth_skin.description",
  },
  {
    id: "increase_realism",
    nameKey: "studio.face.effects.increase_realism.name",
    descriptionKey: "studio.face.effects.increase_realism.description",
  },
  {
    id: "add_imperfections",
    nameKey: "studio.face.effects.add_imperfections.name",
    descriptionKey: "studio.face.effects.add_imperfections.description",
  },
  {
    id: "remove_freckles",
    nameKey: "studio.face.effects.remove_freckles.name",
    descriptionKey: "studio.face.effects.remove_freckles.description",
  },
  {
    id: "add_freckles",
    nameKey: "studio.face.effects.add_freckles.name",
    descriptionKey: "studio.face.effects.add_freckles.description",
  },
];

const MAKEUP_STYLES: Array<{
  id: MakeupStyle;
  nameKey: string;
  descriptionKey: string;
}> = [
  {
    id: "natural",
    nameKey: "studio.face.makeup_styles.natural.name",
    descriptionKey: "studio.face.makeup_styles.natural.description",
  },
  {
    id: "glamorous",
    nameKey: "studio.face.makeup_styles.glamorous.name",
    descriptionKey: "studio.face.makeup_styles.glamorous.description",
  },
  {
    id: "smokey_eye",
    nameKey: "studio.face.makeup_styles.smokey_eye.name",
    descriptionKey: "studio.face.makeup_styles.smokey_eye.description",
  },
  {
    id: "cat_eye",
    nameKey: "studio.face.makeup_styles.cat_eye.name",
    descriptionKey: "studio.face.makeup_styles.cat_eye.description",
  },
  {
    id: "bold_lips",
    nameKey: "studio.face.makeup_styles.bold_lips.name",
    descriptionKey: "studio.face.makeup_styles.bold_lips.description",
  },
  {
    id: "bronzed",
    nameKey: "studio.face.makeup_styles.bronzed.name",
    descriptionKey: "studio.face.makeup_styles.bronzed.description",
  },
  {
    id: "matte",
    nameKey: "studio.face.makeup_styles.matte.name",
    descriptionKey: "studio.face.makeup_styles.matte.description",
  },
];

export const StudioFace = ({ image, onAction }: StudioFaceProps) => {
  const { t } = useTranslation();
  const [selectedEffect, setSelectedEffect] = useState<FaceEffectType | null>(
    null
  );
  const [selectedMakeup, setSelectedMakeup] = useState<MakeupStyle | null>(
    null
  );
  const [customMakeup, setCustomMakeup] = useState("");
  const [count, setCount] = useSavedState("face-count", 1);
  const [isProcessing, setIsProcessing] = useState(false);
  const { faceEffect } = useImage(image.id);

  const handleEffectSelect = (effectId: FaceEffectType) => {
    if (selectedEffect === effectId) {
      setSelectedEffect(null);
    } else {
      setSelectedEffect(effectId);
      setSelectedMakeup(null); // Deselect makeup when selecting an effect
    }
  };

  const handleMakeupSelect = (makeupId: MakeupStyle) => {
    if (selectedMakeup === makeupId) {
      setSelectedMakeup(null);
    } else {
      setSelectedMakeup(makeupId);
      setSelectedEffect("makeup"); // Select makeup effect
    }
  };

  const handleApplyEffect = async () => {
    if (!selectedEffect) return;

    setIsProcessing(true);
    try {
      const makeupDescription =
        selectedEffect === "makeup"
          ? customMakeup ||
            (selectedMakeup
              ? t(`studio.face.makeup_styles.${selectedMakeup}.name`)
              : undefined)
          : undefined;
      await faceEffect(selectedEffect, makeupDescription, count);
      onAction(`apply_face_${selectedEffect}`);
    } catch (error) {
      console.error("Failed to apply face effect:", error);
    }
    setIsProcessing(false);
  };

  const canApply =
    selectedEffect &&
    (selectedEffect !== "makeup" || selectedMakeup || customMakeup);

  return (
    <div className="space-y-4">
      <Heading size="3">{t("studio.face.title")}</Heading>

      {/* Face Effects Grid */}
      <div className="space-y-2">
        <Heading size="2">{t("studio.face.effects_label")}</Heading>
        <div className="-mx-8 px-4">
          <EnhancedScrollArea className="w-full">
            <div className="flex gap-4 px-4 py-2">
              {FACE_EFFECTS.map((effect) => (
                <Tooltip key={effect.id} content={t(effect.descriptionKey)}>
                  <div className="w-24 h-28 text-center space-y-2">
                    <div
                      className={`cursor-pointer rounded-lg p-3 transition-all w-24 h-24 flex items-center justify-center ${
                        selectedEffect === effect.id
                          ? "ring-2 ring-blue-500 ring-offset-0"
                          : ""
                      }`}
                      style={{
                        backgroundImage: `url("/images/face-effects/${effect.id}.jpg")`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        backgroundColor: "#f1f5f9",
                      }}
                      onClick={() => handleEffectSelect(effect.id)}
                    />
                    <Text
                      size="1"
                      className="block whitespace-nowrap text-ellipsis overflow-hidden w-24"
                    >
                      {t(effect.nameKey)}
                    </Text>
                  </div>
                </Tooltip>
              ))}
            </div>
          </EnhancedScrollArea>
        </div>
      </div>

      {/* Makeup Styles Grid */}
      <div className="space-y-2">
        <Heading size="2">{t("studio.face.makeup_label")}</Heading>
        <div className="-mx-8 px-4">
          <EnhancedScrollArea className="w-full">
            <div className="flex gap-4 px-4 py-2">
              {MAKEUP_STYLES.map((makeup) => (
                <Tooltip key={makeup.id} content={t(makeup.descriptionKey)}>
                  <div className="w-24 h-28 text-center space-y-2">
                    <div
                      className={`cursor-pointer rounded-lg p-3 transition-all w-24 h-24 flex items-center justify-center ${
                        selectedMakeup === makeup.id
                          ? "ring-2 ring-blue-500 ring-offset-0"
                          : ""
                      }`}
                      style={{
                        backgroundImage: `url("/images/makeup-styles/${makeup.id}.jpg")`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        backgroundColor: "#f1f5f9",
                      }}
                      onClick={() => handleMakeupSelect(makeup.id)}
                    />
                    <Text
                      size="1"
                      className="block whitespace-nowrap text-ellipsis overflow-hidden w-24"
                    >
                      {t(makeup.nameKey)}
                    </Text>
                  </div>
                </Tooltip>
              ))}
            </div>
          </EnhancedScrollArea>
        </div>
      </div>

      {/* Custom Makeup Description */}
      {selectedEffect === "makeup" && (
        <div className="space-y-2">
          <Heading size="2">{t("studio.face.custom_makeup_label")}</Heading>
          <TextArea
            placeholder={t("studio.face.custom_makeup_placeholder")}
            value={customMakeup}
            onChange={(e) => setCustomMakeup(e.target.value)}
            rows={2}
          />
        </div>
      )}

      {/* Count Selector + Apply Button */}
      <div className="flex space-x-2 w-full">
        <ResponsiveSelect
          size="2"
          value={count.toString()}
          onChange={(value: string) => setCount(parseInt(value))}
          items={[
            { value: "1", label: "1" },
            { value: "2", label: "2" },
            { value: "4", label: "4" },
          ]}
          renderTrigger={() => (
            <span className="text-xs uppercase text-gray-500">{count}</span>
          )}
        />
        <div className="grow">
          <Button
            variant="solid"
            size="2"
            className="w-full"
            disabled={!canApply || isProcessing}
            onClick={handleApplyEffect}
          >
            <span>
              {t("studio.face.apply")} <CoinsVal val={4 * count} />
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
};
